import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { AddItem } from '../../types/actions';
import type { Location } from '../../../shared/types';
import TextAreaField from '../ui/TextAreaField';
import { api } from '../../api/client';
import { parseFormDraftResponse } from './aiFormDraftParser';
import AiDraftAssistPanel from './AiDraftAssistPanel';

export default function NPCForm({
  addItem,
  locations = [],
  submitRef,
  showSubmit = true,
}: {
  addItem: AddItem;
  locations?: Location[];
  submitRef?: React.MutableRefObject<(() => void) | null>;
  showSubmit?: boolean;
}) {
  const [npc, setNpc] = useState({
    name: '',
    description: '',
    location: '',
    locationId: null as number | null,
    notes: '',
  });
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState('');
  const [isDraftingWithAi, setIsDraftingWithAi] = useState(false);

  const draftWithAi = useCallback(async () => {
    if (isDraftingWithAi) return;

    const prompt = aiPrompt.trim();
    const hasCurrentDraft = [npc.name, npc.description, npc.location, npc.notes].some((value) => value.trim().length > 0);
    if (!prompt && !hasCurrentDraft) {
      setAiError('Add a prompt or some draft text before generating.');
      return;
    }

    const knownLocations = locations.slice(0, 24).map((loc) => loc.name).join(', ') || 'none';
    const systemPrompt = `You draft NPC records for tabletop campaigns.
Return ONLY JSON with keys: name, description, location, notes.
Rules:
- Keep each field concise and practical for session prep.
- location should prefer an existing location name when possible.
- Do not include extra keys or markdown fences.`;
    const userPrompt = `Create or refine this NPC draft.
Current draft:
- name: ${npc.name || '(empty)'}
- description: ${npc.description || '(empty)'}
- location: ${npc.location || '(empty)'}
- notes: ${npc.notes || '(empty)'}
Known locations: ${knownLocations}
GM prompt: ${prompt || '(none)'}`;

    setIsDraftingWithAi(true);
    setAiError('');
    try {
      const result = await api.sendAssistantMessage(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        0.6
      );
      const parsed = parseFormDraftResponse(result.content, ['name', 'description', 'location', 'notes']);
      if (!parsed) {
        throw new Error('AI returned an unexpected format. Try a more specific prompt.');
      }

      setNpc((previous) => {
        const nextLocation = parsed.location ?? previous.location;
        const nextLocationId = parsed.location
          ? (locations.find((loc) => loc.name.trim().toLowerCase() === nextLocation.trim().toLowerCase())?.id ?? null)
          : previous.locationId;
        return {
          ...previous,
          name: parsed.name ?? previous.name,
          description: parsed.description ?? previous.description,
          location: nextLocation,
          locationId: nextLocationId,
          notes: parsed.notes ?? previous.notes,
        };
      });
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Failed to generate NPC draft with AI.');
    } finally {
      setIsDraftingWithAi(false);
    }
  }, [aiPrompt, isDraftingWithAi, locations, npc.description, npc.location, npc.name, npc.notes]);

  const handleSubmit = useCallback(() => {
    if (!npc.name.trim()) {
      setErrors({ name: 'NPC name is required.' });
      return;
    }

    addItem('npcs', {
      name: npc.name,
      description: npc.description,
      location: npc.location,
      locationId: npc.locationId,
      notes: npc.notes,
    });
    setNpc({ name: '', description: '', location: '', locationId: null, notes: '' });
    setAiPrompt('');
    setAiError('');
    setErrors({});
  }, [addItem, npc]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
  }, [submitRef, handleSubmit]);

  return (
    <div className="bg-[var(--accent-muted)] p-4 rounded-lg border border-[var(--glass-border)] mb-4">
      <div className="field-block">
        <label className="field-label">NPC Name</label>
        <input
          type="text"
          placeholder="NPC Name"
          value={npc.name}
          onChange={(e) => {
            setNpc({ ...npc, name: e.target.value });
            if (errors.name) setErrors({});
          }}
          className={`input-field ${errors.name ? 'input-error' : ''}`}
        />
        <div className="field-meta">
          <span className={errors.name ? 'field-error' : 'field-hint'}>
            {errors.name || 'Required.'}
          </span>
        </div>
      </div>
      <div className="field-block">
        <label className="field-label">Description</label>
        <input
          type="text"
          placeholder="Description"
          value={npc.description}
          onChange={(e) => setNpc({ ...npc, description: e.target.value })}
          className="input-field"
        />
        <div className="field-meta">
          <span className="field-hint">Short descriptor or role.</span>
        </div>
      </div>
      <div className="field-block">
        <label className="field-label">Location</label>
      <select
        value={npc.locationId ?? ''}
        onChange={(e) => {
          const value = e.target.value;
          if (!value) {
            setNpc({ ...npc, locationId: null, location: '' });
            return;
          }
          const id = parseInt(value, 10);
          const selected = locations.find((loc) => loc.id === id);
          setNpc({
            ...npc,
            locationId: Number.isNaN(id) ? null : id,
            location: selected?.name || '',
          });
        }}
        className="input-field"
      >
        <option value="">Select location</option>
        {locations.map((loc) => (
          <option key={loc.id} value={loc.id}>
            {loc.name}
          </option>
        ))}
      </select>
        <div className="field-meta">
          <span className="field-hint">Choose an existing location.</span>
        </div>
      </div>
      <AiDraftAssistPanel
        value={aiPrompt}
        onChange={(value) => {
          setAiPrompt(value);
          if (aiError) setAiError('');
        }}
        onGenerate={() => void draftWithAi()}
        isGenerating={isDraftingWithAi}
        error={aiError}
        hint="Describe role, motivation, and scene utility. Cmd/Ctrl+Enter also generates."
      />
      <TextAreaField
        label="Notes"
        placeholder="Notes"
        value={npc.notes}
        onChange={(value) => setNpc({ ...npc, notes: value })}
        hint="Add secrets, quirks, and hooks."
        showCount
        minRows={4}
      />
      {showSubmit && (
        <button
          onClick={handleSubmit}
          className="btn-primary-solid flex items-center gap-2"
        >
          <Plus size={16} /> Add NPC
        </button>
      )}
    </div>
  );
}
